import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { Fade, Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { BusinessEntity } from '~/app/pages/Entities/_partials/BusinessEntity';
import { EntityTypeSelector } from '~/app/pages/Entities/_partials/EntityTypeSelector';
import { PersonEntity } from '~/app/pages/Entities/_partials/PersonEntity';
import { ROUTE } from '~/app/routes';
import { CopyInput, EmptyState, Loading, Notification, RenderFields } from '~/components';
import { UnderlineLink } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { useSessionStore } from '~/stores/Session';
import { Hr, Inner } from '~/styles';

import { EntityApiModeProvider, createEntityModeRequestOptions } from './_util/entityApiMode';
import { Mode, RootEntityOutletContext } from './_util/RootEntityContext';
import { EntityFormType } from './_util/useEntityForm';
import { EntityTypes } from './Edit';

const NotificationWrapper = styled.span`
  display: block;
  margin: 24px var(--page-margin) 0;
`;

const EntityStatusNotification: React.FC<{ status?: string; isSandbox: boolean }> = ({ status, isSandbox }) => {
  if (!status) {
    return null;
  }

  if (status === 'VERIFIED') {
    if (isSandbox) {
      return (
        <Notification color="success" variant="light" withClose={false}>
          You've been approved! You can now enter live mode and move real money.
        </Notification>
      );
    }
    return (
      <Notification color="success" variant="light" withClose={false}>
        You've been approved! You can now move real money.
      </Notification>
    );
  }

  if (status === 'MANUAL_REVIEW') {
    return (
      <Notification color="info" variant="light" withClose={false}>
        Our system has flagged that we need to manually review your request. We'll verify this as soon as possible,
        generally within 24 hours.
      </Notification>
    );
  }

  if (status === 'PENDING') {
    return (
      <Notification color="info" variant="light" withClose={false}>
        Your KYC approval is pending. We will email you with updates on your account approval status.
      </Notification>
    );
  }

  if (status === 'DENIED') {
    return (
      <Notification color="danger" variant="light" withClose={false}>
        Unfortunately, this registration was declined. Please{' '}
        <UnderlineLink to={ROUTE.CONTACT_FORM} withoutArrow>
          contact us
        </UnderlineLink>{' '}
        if you have any questions.
      </Notification>
    );
  }

  return null;
};

export const PageRootEntity: React.FC = () => {
  const {
    mode,
    formType,
    sandboxRootEntity,
    liveRootEntity,
    isSandboxLoading,
    isLiveLoading,
    handleSubmit,
    setFormType,
    resetForm,
  } = useOutletContext<RootEntityOutletContext>();
  const navigate = useNavigate();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);

  // Pin the associated-person selector and create-person modal to this root tab's live/sandbox mode
  // (independent of the app's global mode), so people are searched, looked up, and created in the
  // same mode the root entity is saved in — never created Live and then linked in Sandbox.
  const apiMode = useMemo(
    () => ({
      requestOptions: createEntityModeRequestOptions(platformId ?? '', mode === Mode.Live),
      scope: [platformId, mode],
    }),
    [platformId, mode]
  );

  const [displayMode, setDisplayMode] = useState<Mode>(mode);
  const [displayFormType, setDisplayFormType] = useState<EntityTypes>();
  const previousFormTypeRef = useRef<EntityTypes | undefined>(undefined);
  const previousModeRef = useRef<Mode>(mode);

  const rootEntityResponse = displayMode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const isRootEntityLoading = displayMode === Mode.Sandbox ? isSandboxLoading : isLiveLoading;
  const currentModeLoading = mode === Mode.Sandbox ? isSandboxLoading : isLiveLoading;
  const hasRootEntity = Boolean(rootEntityResponse);
  const isLiveRootEntity = displayMode === Mode.Live && hasRootEntity;

  useEffect(() => {
    if (mode !== previousModeRef.current) {
      previousModeRef.current = mode;
    } else if (displayMode !== mode) {
      setDisplayMode(mode);
    }
  }, [mode, displayMode]);

  useEffect(() => {
    if (formType && !displayFormType) {
      setDisplayFormType(formType);
    } else if (!formType && !displayFormType && !isRootEntityLoading && !hasRootEntity) {
      setFormType(EntityFormType.Person);
    }
  }, [formType, displayFormType, isRootEntityLoading, hasRootEntity, setFormType]);

  useEffect(() => {
    if (formType !== previousFormTypeRef.current) {
      previousFormTypeRef.current = formType;
      if (hasRootEntity && formType) {
        setDisplayFormType(formType);
      }
    } else if (displayFormType !== formType && !hasRootEntity && formType) {
      setDisplayFormType(formType);
    }
  }, [formType, hasRootEntity, displayFormType]);

  useEffect(() => {
    if (hasRootEntity && formType) {
      resetForm(formType, rootEntityResponse ?? null);
      previousFormTypeRef.current = formType;
    }
  }, [resetForm, formType, rootEntityResponse, hasRootEntity]);

  const handleModeFadeOut = () => {
    if (mode !== displayMode && formType) {
      const newRootEntityResponse = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
      resetForm(formType, newRootEntityResponse ?? null);
      setDisplayMode(mode);
    }
  };

  const handlePersonFormFadeOut = () => {
    if (!hasRootEntity && formType === EntityFormType.Business && displayFormType === EntityFormType.Person) {
      resetForm(EntityFormType.Business, null);
      setDisplayFormType(EntityFormType.Business);
    }
  };

  const handleBusinessFormFadeOut = () => {
    if (!hasRootEntity && formType === EntityFormType.Person && displayFormType === EntityFormType.Business) {
      resetForm(EntityFormType.Person, null);
      setDisplayFormType(EntityFormType.Person);
    }
  };

  useEffect(() => {
    if (!hasRootEntity && displayFormType) {
      resetForm(displayFormType, null);
    }
  }, [hasRootEntity, displayFormType, resetForm]);

  const isFormInitialized = displayFormType !== undefined;

  return (
    <Loading isLoading={currentModeLoading || !isFormInitialized}>
      <Fade show={mode === displayMode && isFormInitialized} onExited={handleModeFadeOut}>
        {displayMode === Mode.Live && !hasRootEntity && (
          <EmptyState headline="No root entity" text={'Create your Root Entity by opening a Commercial Account.'}>
            <Button onClick={() => navigate(`${PUBLIC_ROUTE.REGISTER_OPEN_BANK_ACCOUNT}`)} size="sm">
              <Icon.Plus />
              Create Commercial Account
            </Button>
          </EmptyState>
        )}

        {(displayMode === Mode.Sandbox || hasRootEntity) && (
          <>
            {displayMode === Mode.Live && hasRootEntity && (
              <NotificationWrapper>
                {/* Only rendered in the Live display mode, so never the sandbox copy. */}
                <EntityStatusNotification status={rootEntityResponse?.verification_status} isSandbox={false} />
              </NotificationWrapper>
            )}

            {RenderFields<{ type: EntityTypes; id?: string }>({
              sections: [
                {
                  fields: [
                    {
                      id: 'id',
                      label: 'ID',
                      hide: !hasRootEntity,
                      children: ({ value }) => <CopyInput value={(value as string) ?? ''} isMonospaced />,
                    },
                    {
                      id: 'type',
                      label: 'Entity Type',
                      children: () => (
                        <EntityTypeSelector
                          type={formType}
                          isReadOnly={hasRootEntity}
                          onSelect={(next) => setFormType(next)}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
            <Hr />

            <EntityApiModeProvider value={apiMode}>
              <Fade
                show={
                  displayFormType === EntityFormType.Person && (hasRootEntity || formType === EntityFormType.Person)
                }
                onExited={handlePersonFormFadeOut}
              >
                {displayFormType === EntityFormType.Person && (
                  <PersonEntity
                    id={rootEntityResponse?.person_details ? rootEntityResponse.id : undefined}
                    disableForm={isLiveRootEntity}
                  />
                )}
              </Fade>
              <Fade
                show={
                  displayFormType === EntityFormType.Business && (hasRootEntity || formType === EntityFormType.Business)
                }
                onExited={handleBusinessFormFadeOut}
              >
                {displayFormType === EntityFormType.Business && (
                  <BusinessEntity
                    id={rootEntityResponse?.business_details ? rootEntityResponse.id : undefined}
                    disableForm={isLiveRootEntity}
                    isRoot
                  />
                )}
              </Fade>
            </EntityApiModeProvider>

            {!isLiveRootEntity && (
              <Inner pt={0}>
                <Button onClick={handleSubmit} size="sm">
                  {!hasRootEntity && <Icon.Plus />}
                  {hasRootEntity ? 'Save Changes' : 'Create Root Entity'}
                </Button>
              </Inner>
            )}
          </>
        )}
      </Fade>
    </Loading>
  );
};
